import React, { useEffect, useRef, useState } from "react";

function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return "00:00";

  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function DemoAudio({
  src,
  label = "Demo de audio",
}) {
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

const bars = [
  10, 18, 12, 24, 17, 30, 21, 14,
  26, 35, 19, 28, 15, 24, 11, 20,
  29, 16, 25, 12, 18, 31, 20, 14,
  28, 17, 33, 20, 13, 25, 19, 30,
  15, 22, 34, 18, 27, 13, 24, 31,
  17, 28, 21, 14, 26, 19, 32, 16
];

  const progress =
    duration > 0
      ? (currentTime / duration) * 100
      : 0;

  const togglePlay = async () => {
    const audio = audioRef.current;

    if (!audio) return;

    try {
      if (audio.paused) {
        await audio.play();
      } else {
        audio.pause();
      }
    } catch (error) {
      console.error("No fue posible reproducir el audio:", error);
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;

    if (!audio || !duration) return;

    const bounds =
      event.currentTarget.getBoundingClientRect();

    const percentage =
      (event.clientX - bounds.left) /
      bounds.width;

    const safePercentage =
      Math.min(
        Math.max(percentage, 0),
        1
      );

    audio.currentTime =
      safePercentage * duration;
  };

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener(
      "loadedmetadata",
      handleLoadedMetadata
    );

    audio.addEventListener(
      "timeupdate",
      handleTimeUpdate
    );

    audio.addEventListener(
      "play",
      handlePlay
    );

    audio.addEventListener(
      "pause",
      handlePause
    );

    audio.addEventListener(
      "ended",
      handleEnded
    );

    return () => {
      audio.removeEventListener(
        "loadedmetadata",
        handleLoadedMetadata
      );

      audio.removeEventListener(
        "timeupdate",
        handleTimeUpdate
      );

      audio.removeEventListener(
        "play",
        handlePlay
      );

      audio.removeEventListener(
        "pause",
        handlePause
      );

      audio.removeEventListener(
        "ended",
        handleEnded
      );
    };
  }, []);

  return (
    <div
      className={`demo-audio ${
        isPlaying
          ? "demo-audio--playing"
          : ""
      }`}
    >

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
      />


      <button
        type="button"
        className="demo-audio__play"
        onClick={togglePlay}
        aria-label={
          isPlaying
            ? `Pausar ${label}`
            : `Reproducir ${label}`
        }
      >
        <span className="demo-audio__play-icon">
          {isPlaying ? "❚❚" : "▶"}
        </span>
      </button>


      <div
        className="demo-audio__waveform"
        onClick={handleSeek}
        role="slider"
        tabIndex={0}
        aria-label={`Progreso de ${label}`}
        aria-valuemin={0}
        aria-valuemax={Math.floor(duration)}
        aria-valuenow={Math.floor(currentTime)}
      >

        {bars.map((height, index) => {
          const barPosition =
            ((index + 1) / bars.length) * 100;

          const played =
            barPosition <= progress;

          return (
            <span
              key={index}
              className={`demo-audio__bar ${
                played
                  ? "demo-audio__bar--played"
                  : ""
              }`}
              style={{
                "--audio-bar-height": `${height}px`,
                "--audio-bar-index": index,
              }}
            />
          );
        })}

      </div>


      <div className="demo-audio__time">
        <span>
          {formatTime(currentTime)}
        </span>

        <span className="demo-audio__time-separator">
          /
        </span>

        <span>
          {formatTime(duration)}
        </span>
      </div>

    </div>
  );
}

export default DemoAudio;